import { getInspectionIconColour } from "./inspection";

const BASE_HEIGHT = 900;   // reference viewport height (px)
const BASE_ZOOM = 3.2;   // zoom that touches top & bottom at BASE_HEIGHT

const MIN_LEFT_PADDING = window.innerWidth * 1; // small screen
const MAX_LEFT_PADDING = window.innerWidth * 0.62; // large screen
const MIN_WIDTH = 900; // window width where max padding is reached
const MAX_WIDTH = 2200; // window width where min padding is reached

const BATTERY_THRESHOLD = 25;

function computeGlobeZoom() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Skip mobile
    if (width < 900) {
        return 2;
    }

    // Logarithmic scaling (Mapbox zoom is log2-based)
    const scale = height / BASE_HEIGHT;
    const zoom = BASE_ZOOM + Math.log2(scale);

    return zoom;
}

function computeLeftPadding() {
    const width = window.innerWidth;

    if (width < MIN_WIDTH) return MIN_LEFT_PADDING;
    if (width > MAX_WIDTH) return MAX_LEFT_PADDING;

    // Linear interpolation
    const t = (width - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
    return MIN_LEFT_PADDING + t * (MAX_LEFT_PADDING - MIN_LEFT_PADDING);
}

function updateCamera(map) {
    const zoom = computeGlobeZoom();
    map.current.setZoom(zoom);

    const leftPadding = computeLeftPadding();
    map.current.setPadding({
        left: leftPadding,
        right: 0,
        top: 0,
        bottom: 0
    });
}

function setMapIcons(map, isVisible) {
    if (!map.current) return;
    const style = map.current.getStyle();
    if (!style) return;

    const layers = style.layers;
    // console.log(layers)
    const container = map.current.getContainer();
    const visibility = isVisible ? 'visible' : 'none';

    layers.forEach((layer) => {
        if (layer.type === 'symbol') {
            map.current.setLayoutProperty(layer.id, 'visibility', visibility);
        }
    });

    if (isVisible) {
        container.classList.remove('hide-user-location');
    } else {
        container.classList.add('hide-user-location');
    }
}

function enableInteractivity(map) {
    map.current.boxZoom.enable();
    map.current.doubleClickZoom.enable();
    map.current.dragPan.enable();
    map.current.dragRotate.enable();
    map.current.keyboard.enable();
    map.current.scrollZoom.enable();
    map.current.touchZoomRotate.enable();
    map.current.interactive = true;
    map.current.spinEnabled = false;
}

function disableInteractivity(map) {
    map.current.boxZoom.disable();
    map.current.doubleClickZoom.disable();
    map.current.dragPan.disable();
    map.current.dragRotate.disable();
    map.current.keyboard.disable();
    map.current.scrollZoom.disable();
    map.current.touchZoomRotate.disable();
    map.current.interactive = false;
    map.current.setBearing(0);
    map.current.setPitch(0);
}

function adjustSpin(map) {
    var rotationSpeed = 1;
    var origin = map.current.interactiveCenter;

    function spin() {
        if (!map.current.interactive) {
            origin.lng -= rotationSpeed / 60;
            map.current.setCenter(origin, { animate: false });
            requestAnimationFrame(spin);
        }
        else {
            map.current.setCenter(map.current.originalCenter, { animate: false });
            map.current.interactiveCenter = map.current.getCenter();
        }
    }

    if (!map.current.spinEnabled) {
        map.current.spinEnabled = true;

        origin.lng += 20;
        spin();
    }
}

function enableMap(map, user, selectedOrg) {
    map.current.orgCenter = null;
    map.current.originalCenter = Object.values(user?.organisations.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 }).reverse();
    map.current.setZoom(5); //INITIAL ZOOM POINT FOR OVERVIEW MAP

    enableInteractivity(map);
    adjustSpin(map);
    setMapIcons(map, true);
}

function disableMap(map, user, selectedOrg) {
    const coords = user?.organisations.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 };

    if (map.current?.orgCenter != coords) {
        map.current.orgCenter = coords;

        map.current.setCenter(Object.values(coords).reverse());
        map.current.originalCenter = map.current.getCenter();
        map.current.interactiveCenter = map.current.getCenter();
        map.current.spinEnabled = false;
    }

    disableInteractivity(map);
    updateCamera(map);
    adjustSpin(map);
    setMapIcons(map, false);
}

function setDevicePointColour(device, showAdvancedDeviceLevel, mapOption, isAttentionMode) {
    var opacity = 1;
    var zIndex_set = undefined;

    if (mapOption?.category == 'Maintenance Work') {
        if (device.maintenance_tags.includes(mapOption.label)) {
            return { feature_colour: mapOption?.colour, zIndex: 1, opacity }
        }
        else {
            return { feature_colour: '#aaaaaa', zIndex: 2, opacity };
        }
    }
    else if (["Devices", "Sites", "Stops"].indexOf(mapOption?.category) > -1) {
        opacity = device.uuid != mapOption.uuid ? 0.2 : 1;
        zIndex_set = device.uuid == mapOption.uuid ? 10 : undefined;
    }
    else if (mapOption?.category == "Routes") {
        if (mapOption.stop_ids.includes(device.site_id)) {
            return { feature_colour: `#${mapOption.colour}`, zIndex: 2, opacity }
        }
        else {
            return { feature_colour: '#aaaaaa', zIndex: 1, opacity: 0.2 };
        }
    }

    if (isAttentionMode) {
        if (device?.inspection_record_id) {
            return { feature_colour: getInspectionIconColour(device.status).hex, attention_colour: '#ff0000', zIndex: 2, opacity }
        }
        else {
            return { feature_colour: '#aaaaaa', attention_colour: '#ffffff', zIndex: 1, opacity: 0.2 };
        }
    }

    if (!device.active && showAdvancedDeviceLevel) {
        return { feature_colour: '#aaaaaa', zIndex: zIndex_set || 1, opacity };
    }
    else {
        if (device.connected_state == 'sleeping') {
            return { feature_colour: "#555", zIndex: zIndex_set || 5, opacity };
        }
        else if (device.connected_state == 'offline') {
            return { feature_colour: "#ff667c", zIndex: zIndex_set || 4, opacity };
        }
        else if (device.battery <= BATTERY_THRESHOLD && showAdvancedDeviceLevel) {
            return { feature_colour: "#edaa00", zIndex: zIndex_set || 3, opacity };
        }
        else {
            return { feature_colour: "#58ba2d", zIndex: zIndex_set || 2, opacity };
        }
    }
}

function setGroupPolygonColour(group, showAdvancedDeviceLevel) {
    if (group.organisation_id != 'clearways') {
        return "#007add";
    }

    if (!group.all_updated) {
        return "#ff667c";
    }
    else if (group.devices_monitored > 0 && showAdvancedDeviceLevel) {
        return "#edaa00";
    }
    else {
        return "#58ba2d";
    }
}

function setCameraDeviceIcon(device, showAdvancedDeviceLevel) {
    if (!device.active && showAdvancedDeviceLevel) {
        var icon_image = 'cameraIconInactive';
    }
    else {
        if (device.connected_state == 'offline') {
            var icon_image = 'cameraIconOffline';
        }
        else if (device.battery <= BATTERY_THRESHOLD && showAdvancedDeviceLevel) {
            var icon_image = 'cameraIconWarning';
        }
        else {
            var icon_image = 'cameraIconOnline';
        }
    }

    return icon_image;
}

function setGroupFeatures(group, showAdvancedDeviceLevel, parent_group = false) {
    const { geojson, ...metadata } = group;

    var group_features = geojson;

    group_features.forEach((feature) => {
        feature.properties = {
            ...metadata,
            access: showAdvancedDeviceLevel,
            feature_colour: parent_group ? null : setGroupPolygonColour(group, showAdvancedDeviceLevel)
        };
    });

    return group_features;
}

function setDeviceFeatures(device, showAdvancedDeviceLevel, mapOption = {}, isAttentionMode = false) {
    var ret = {
        type: "Feature",
        properties: {
            ...device,
            access: showAdvancedDeviceLevel,
            ...setDevicePointColour(device, showAdvancedDeviceLevel, mapOption, isAttentionMode)
        },
        geometry: {
            type: 'Point',
            coordinates: device.position
        }
    }

    if (!isAttentionMode) {
        if (device.hardware_type == "bk-camera") {
            ret.properties.icon = setCameraDeviceIcon(device, showAdvancedDeviceLevel);
        }

        if (device.connected_state == "sleeping") {
            ret.properties.icon = 'deviceIconSleeping';
        }
        else if (device.connected_state == 'eco' && device.battery <= BATTERY_THRESHOLD && showAdvancedDeviceLevel) {
            ret.properties.icon = 'deviceIconEcoModeWarning';
        }
        else if (device.connected_state == 'eco') {
            ret.properties.icon = 'deviceIconEcoMode';
        }
    }

    return ret;
}

function setSiteFeatures(site, showAdvancedDeviceLevel) {
    return {
        type: "Feature",
        properties: {
            ...site,
            access: showAdvancedDeviceLevel,
            zIndex: 1,
            opacity: 1
        },
        geometry: {
            type: 'Point',
            coordinates: [site.position.longitude, site.position.latitude]
        }
    }
}

export {
    enableInteractivity, disableInteractivity, setMapIcons, adjustSpin, enableMap, disableMap, updateCamera, setDevicePointColour, setGroupPolygonColour, setCameraDeviceIcon, setGroupFeatures, setDeviceFeatures, setSiteFeatures
}